import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { LOYALTY_TERMS_AND_CONDITIONS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { MessageDescriptor } from 'react-intl'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

const bulletMessages = defineMessages({
  blocks: {
    id: 'WMckWcDXOW7VYUsy_heJ',
    defaultMessage: 'Points can be purchased in blocks of 1,000 up to a maximum of 500,000 Société Points per transaction, excluding bonus offers.',
    description: 'Buy Points "What you need to know" bullet — purchase block size + per-transaction maximum',
  },
  cap: {
    id: '2mxKKEAqBeUXl1ngxzPQ',
    defaultMessage: 'Members may purchase a maximum of 1,000,000 Société Points within any rolling 12-month period, excluding bonus offers.',
    description: 'Buy Points "What you need to know" bullet — rolling-window cap on total points purchased',
  },
  refund: {
    id: 'kp4CwY_dAu1haiI_ax5r',
    defaultMessage: 'Purchased Société Points are non-refundable and cannot be exchanged for cash.',
    description: 'Buy Points "What you need to know" bullet — refundability restriction',
  },
  pricing: {
    id: 'rQ3mf5ZFayZk9vfnA-Iq',
    defaultMessage: 'Pricing is subject to change at any time without notice.',
    description: 'Buy Points "What you need to know" bullet — pricing-change disclaimer',
  },
  gst: {
    id: 'ODZdnhy1H3RDtLapbn5Q',
    defaultMessage: 'Total amount payable includes GST.',
    description: 'Buy Points "What you need to know" bullet shown only in AU — clarifies the displayed total includes GST',
  },
})

interface Bullet {
  message: MessageDescriptor
  /** When set, the bullet is only shown for these region codes. */
  regions?: ReadonlyArray<string>
}

const BULLETS: ReadonlyArray<Bullet> = [
  { message: bulletMessages.blocks },
  { message: bulletMessages.cap },
  { message: bulletMessages.refund },
  { message: bulletMessages.pricing },
  { message: bulletMessages.gst, regions: ['AU'] },
]

function LoyaltyPurchasePointsWhatYouNeedToKnow() {
  const intl = useIntl()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  const visibleBullets = BULLETS.filter(bullet => !bullet.regions || bullet.regions.includes(regionCode))

  return <Group direction="vertical" gap={8}>
    <Heading variant="heading2" align="center">{intl.formatMessage({
      id: 'vjnr5wDkrrwmTto5XM5-',
      defaultMessage: 'What you need to know',
      description: 'Heading for the FAQ / constraints section on the Buy Points landing page',
    })}
    </Heading>
    <Group as="ul" direction="vertical" gap={8} horizontalAlign="start">
      {visibleBullets.map(bullet => (
        <li key={bullet.message.id}>
          <BodyText variant="medium">
            {intl.formatMessage(bullet.message)}
          </BodyText>
        </li>
      ))}
    </Group>
    <Group direction="vertical" fullWidth horizontalAlign="start">
      <BodyText variant="medium">
        <FormattedMessage
          id="NAHd5_fcmUQSYiuq1yqv"
          defaultMessage="<link>Terms and conditions</link>"
          description="Terms and conditions link below the FAQ bullets on the Buy Points landing page"
          values={{
            link: (chunks) => (
              <TextLink to={`/${LOYALTY_TERMS_AND_CONDITIONS_PAGE}`} target="_blank" rel="noopener noreferrer">
                {chunks}
              </TextLink>
            ),
          }}
        />
      </BodyText>
    </Group>
  </Group>
}

export default LoyaltyPurchasePointsWhatYouNeedToKnow
